import type { StayReservation, StaySource, StaysPolicy } from '../../../interfaces/stays';
import type { PillTone } from '../dashboard/dashboard-stays-components/stays-labels';
import { plural } from '../../../function-utils/plural';
import {
  Clock,
  clockTime,
  daysBetween,
  deviceZone,
  formatDayKey,
  isDayKey,
  usableZone,
} from '../../../function-utils/building-time';

/**
 * LOBBERO STAYS — the host's stay list, as pure words (ai-plans/stays plan S3).
 *
 * THREE CLOCKS, NEVER MIXED:
 *   - check_in / check_out are BUILDING calendar day keys ('2026-11-20'). They
 *     are formatted as they are: built at T12:00:00Z and printed in UTC, so no
 *     device zone can move them a day (a JS Date at local midnight would).
 *   - expected_arrival_time / check_in_time are BUILDING wall-clock times, shown
 *     as stored, under a ClockNote data-clock-whose="building".
 *   - "Signed in HH:MM" is an INSTANT, printed on the DEVICE clock (owners and
 *     managers are on their own clock, R-T12); the element carries data-at.
 */

const DAY_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;

export const isStayDayKey = (value: unknown): value is string =>
  typeof value === 'string' && DAY_KEY.test(value) && !Number.isNaN(Date.parse(`${value}T12:00:00Z`));

/** "Fri 20 Nov" for '2026-11-20'. Anything that is not a day key comes back as it was. */
// Through building-time's one day-key formatter (UTC, en-GB: "Fri 20 Nov").
export const formatStayDay = (key: string): string =>
  isStayDayKey(key) && isDayKey(key) ? formatDayKey(key, { weekday: 'short', day: 'numeric', month: 'short' }) || key : key;

/** "2 nights" / "1 night". */
export const nightsText = (n: number): string => `${n} ${plural(n, 'night')}`;

/** Nights between two day keys, or null while either is missing or out of order. */
export const nightsBetween = (checkIn: string, checkOut: string): number | null => {
  if (!isStayDayKey(checkIn) || !isStayDayKey(checkOut)) return null;
  const n = daysBetween(checkIn, checkOut);

  return Number.isFinite(n) && n > 0 ? n : null;
};

/** "Fri 20 Nov → Sun 22 Nov · 2 nights" */
export const datesLine = (r: Pick<StayReservation, 'check_in' | 'check_out' | 'nights'>): string =>
  `${formatStayDay(r.check_in)} → ${formatStayDay(r.check_out)} · ${nightsText(r.nights)}`;

/** "Fri 20 Nov–Sun 22 Nov" — the dates of a clashing stay (never its guest). */
export const rangeText = (checkIn: string, checkOut: string): string =>
  `${formatStayDay(checkIn)}–${formatStayDay(checkOut)}`;

/** "15:30" when the host set an arrival time, else "From 15:00" (the listing's check-in time). */
export const arrivalText = (r: Pick<StayReservation, 'expected_arrival_time' | 'check_in_time'>): string =>
  r.expected_arrival_time ? r.expected_arrival_time : `From ${r.check_in_time}`;

/** "2 guests" */
export const partyText = (n: number): string => `${n} ${plural(n, 'guest')}`;

export const guestName = (r: Pick<StayReservation, 'lead'>): string =>
  [r.lead.first_name, r.lead.last_name].filter(Boolean).join(' ');

export const firstName = (r: Pick<StayReservation, 'lead'>): string => r.lead.first_name || 'the guest';

export const SOURCE_OPTIONS: ReadonlyArray<{ value: StaySource; label: string }> = [
  { value: 'direct', label: 'Direct' },
  { value: 'airbnb', label: 'Airbnb' },
  { value: 'booking', label: 'Booking.com' },
  { value: 'vrbo', label: 'VRBO' },
  { value: 'other', label: 'Other' },
];

export const sourceLabel = (source: StaySource): string =>
  (SOURCE_OPTIONS.find(o => o.value === source) || { label: source }).label;

/** The building's clock, for the ClockNote. A building with no usable zone is
 *  said to be on UTC, never silently on the reader's own clock. */
export const buildingClock = (zone: string | null | undefined): Clock => {
  const usable = usableZone(zone);

  return usable ? { zone: usable, whose: 'building', missing: null } : { zone: 'UTC', whose: 'building', missing: 'utc' };
};

export interface CheckinPill {
  tone: PillTone;
  text: string;
  /** The ISO instant behind a "Signed in HH:MM" pill (data-at), else null. */
  at: string | null;
}

const STATE_PILL: Record<StayReservation['checkin']['state'], { tone: PillTone; text: string }> = {
  not_started: { tone: 'neutral', text: 'Not started' },
  in_progress: { tone: 'warning', text: 'ID check in progress' },
  failed: { tone: 'alert', text: 'ID check failed' },
  verified: { tone: 'success', text: 'ID verified' },
};

/** IS0 (R-IS0.6): the same states for an in-house ID check — never "verified". */
const STATE_PILL_INHOUSE: Record<StayReservation['checkin']['state'], { tone: PillTone; text: string }> = {
  not_started: { tone: 'neutral', text: 'Not started' },
  in_progress: { tone: 'warning', text: 'ID being added' },
  failed: { tone: 'alert', text: 'Asked to scan the ID again' },
  verified: { tone: 'success', text: 'IDs done' },
};

/**
 * The Check-in column's pill. An arrival wins ("Signed in 15:42 by Ana", on
 * the device clock). Otherwise the online check-in state; under the
 * every-adult policy a party of several adults also says how many IDs are in.
 * `policy` is the entitlement's — the reservation's own copy is the fallback.
 */
export const checkinPill = (
  r: Pick<StayReservation, 'checkin' | 'arrival'>,
  policy?: Pick<StaysPolicy, 'who_verifies'> | null,
  zone: string = deviceZone()
): CheckinPill => {
  if (r.arrival) {
    const time = clockTime(r.arrival.signed_in_at, zone);
    const by = r.arrival.signed_in_by_name ? ` by ${r.arrival.signed_in_by_name}` : '';

    return { tone: 'brand', text: `Signed in ${time}${by}`, at: r.arrival.signed_in_at };
  }
  const inhouse = r.checkin.id_flow === 'inhouse';
  const table = inhouse ? STATE_PILL_INHOUSE : STATE_PILL;
  const base = table[r.checkin.state] || table.not_started;
  const who = policy?.who_verifies || r.checkin.who_verifies;
  const expected = r.checkin.adults_expected;
  const suffix =
    who === 'every_adult' && typeof expected === 'number' && expected > 1 && r.checkin.state !== 'verified'
      ? ` · ${r.checkin.verified} of ${expected} IDs ${inhouse ? 'done' : 'verified'}`
      : '';

  return { tone: base.tone, text: `${base.text}${suffix}`, at: null };
};

/** "Check-in was incomplete" when the desk signed the guest in without a finished check-in. */
export const arrivalHelper = (r: Pick<StayReservation, 'arrival'>): string | null =>
  r.arrival && r.arrival.checkin_state === 'incomplete' ? 'Check-in was incomplete' : null;

/** The incomplete_alert_hours switch as a number, or null when it is not a usable one. */
export const alertHours = (policy?: Pick<StaysPolicy, 'incomplete_alert_hours'> | null): number | null => {
  const n = Number(policy?.incomplete_alert_hours);

  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * A confirmed stay whose guest has not finished check-in and arrives within
 * the next `hours` hours: the helper line under the pill ("Arrives in 5 hours —
 * check-in not finished. Send the link again."). Null otherwise.
 */
export const arrivingSoonText = (
  r: Pick<StayReservation, 'status' | 'arrival' | 'checkin' | 'arrival_at'>,
  hours: number | null,
  now: number = Date.now()
): string | null => {
  if (hours === null || r.status !== 'confirmed' || r.arrival || r.checkin.state === 'verified') return null;
  const at = Date.parse(r.arrival_at);
  if (!Number.isFinite(at)) return null;
  const diff = at - now;
  if (diff <= 0 || diff > hours * 3600000) return null;
  const n = Math.round(diff / 3600000);
  const when = diff < 3600000 || n < 1 ? 'less than an hour' : `${n} ${plural(n, 'hour')}`;

  return `Arrives in ${when} — check-in not finished. Send the link again.`;
};

/** A stay the host can still cancel or send a link for. */
export const isOpenStay = (r: Pick<StayReservation, 'status' | 'arrival'>): boolean =>
  r.status === 'confirmed' && !r.arrival;
